// Module 1 — per-user units preference.
//
// Reads GET /api/sim/preferences on mount and writes back on change via
// PATCH. The hook returns a stable object that every <ParamField> can
// call to resolve the preferred display unit for its kind:
//
//     const { system, setSystem, unitForKind } = useUnits();
//     const u = unitForKind('altitude');   // 'm-alt' | 'ft-alt'
//
// The preference is cached per-tab in state so flipping the radio in
// one <ParamField> propagates instantly to every other field without a
// round-trip. The PATCH is fire-and-forget with a 1.2s debounce — we
// still update server-side so the setting survives logout.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { defaultUnitFor, SYSTEM_DEFAULTS } from "@/lib/units";
import { sim } from "@/api/endpoints";

const DEBOUNCE_MS = 1200;

export default function useUnits({ autoLoad = true } = {}) {
    const [system, setSystemState] = useState("metric");
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const timer = useRef(null);

    useEffect(() => {
        if (!autoLoad) {
            setLoaded(true);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const r = await sim.preferences.get();
                if (cancelled) return;
                if (r?.units_system) setSystemState(r.units_system);
            } catch {
                // Not fatal — we fall back to 'metric' and let the UI keep working.
            } finally {
                if (!cancelled) setLoaded(true);
            }
        })();
        return () => {
            cancelled = true;
            if (timer.current) clearTimeout(timer.current);
        };
    }, [autoLoad]);

    const persist = useCallback((next) => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(async () => {
            setSaving(true);
            try {
                await sim.preferences.patch({ units_system: next });
            } catch {
                // Silent — UI already updated; stale server value will be
                // reconciled on the next page load.
            } finally {
                setSaving(false);
            }
        }, DEBOUNCE_MS);
    }, []);

    const setSystem = useCallback(
        (next) => {
            if (next !== "metric" && next !== "imperial") return;
            setSystemState(next);
            if (loaded) persist(next);
        },
        [loaded, persist],
    );

    const unitForKind = useCallback(
        (kind) => defaultUnitFor(kind, system),
        [system],
    );

    const systems = useMemo(() => Object.keys(SYSTEM_DEFAULTS), []);

    return { system, setSystem, unitForKind, systems, loaded, saving };
}
